import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gamePlayService } from '../../services/db';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../constants/routes';
import Spinner from '../../components/ui/Spinner';
import Button from '../../components/ui/Button';
import {
  TrophyIcon,
  StreakFireIcon,
  LightningIcon,
  CalendarIcon,
  ClockIcon,
  PlayIcon,
  MedalBadge,
} from '../../components/common/Icons';

const GameLeaderboardPage = () => {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect unauthenticated visitors to About Us
  useEffect(() => {
    if (!user) {
      navigate(ROUTES.PUBLIC.HOME, { replace: true });
    }
  }, [user, navigate]);

  const [season, setSeason] = useState(null);
  const [standings, setStandings] = useState([]);
  const [metric, setMetric] = useState('points'); // 'points' | 'streak'
  const [timeframe, setTimeframe] = useState('currentWeek'); // 'currentWeek' | 'pastWeek' | 'currentMonth' | 'pastMonth'
  const [seasonTimeframe, setSeasonTimeframe] = useState('overall'); // 'weekly' | 'monthly' | 'overall' (for seasons)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadLeaderboard = async () => {
      setLoading(true);
      try {
        if (seasonId) {
          const [seasonData, list] = await Promise.all([
            gamePlayService.getSeasonDetails(seasonId),
            gamePlayService.getSeasonLeaderboard(seasonId, seasonTimeframe),
          ]);
          if (isMounted) {
            setSeason(seasonData);
            setStandings(list || []);
          }
        } else {
          const list = await gamePlayService.getGlobalLeaderboard({
            metric,
            timeframe,
          });
          if (isMounted) {
            setSeason(null);
            setStandings(list || []);
          }
        }
      } catch (err) {
        console.error('Failed to load leaderboard:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadLeaderboard();
    return () => {
      isMounted = false;
    };
  }, [seasonId, metric, timeframe, seasonTimeframe]);

  const top3 = standings.slice(0, 3);
  const currentUserEntry = user?.uid ? standings.find((u) => u.userId === user.uid) : null;

  // Arrange Top 3 in Olympic order for visual prominence: 2nd (left), 1st (center), 3rd (right)
  let podiumPlayers = [];
  if (top3.length === 3) {
    podiumPlayers = [
      { ...top3[1], rank: top3[1].rank || 2, medal: <MedalBadge rank={2} size={28} />, cardClass: 'rank-2' },
      { ...top3[0], rank: top3[0].rank || 1, medal: <MedalBadge rank={1} size={32} />, cardClass: 'rank-1' },
      { ...top3[2], rank: top3[2].rank || 3, medal: <MedalBadge rank={3} size={28} />, cardClass: 'rank-3' },
    ];
  } else {
    podiumPlayers = top3.map((p, idx) => {
      const r = p.rank || idx + 1;
      return {
        ...p,
        rank: r,
        medal: <MedalBadge rank={r} size={r === 1 ? 32 : 28} />,
        cardClass: `rank-${r}`,
      };
    });
  }


  return (
    <div className="ranking-page-container">
      {/* Back Button */}
      {seasonId && (
        <button
          type="button"
          onClick={() => navigate(`/play/season/${seasonId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--clr-muted)',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 0',
            marginBottom: '16px',
          }}
        >
          <span>←</span> Back to {season?.season || 'Tournament'}
        </button>
      )}

      {/* Header */}
      <div className="ranking-header">
        <div className="ranking-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <TrophyIcon size={14} style={{ color: '#D97706' }} />
          <span>{season ? 'League Standings' : 'Official Leaderboards'}</span>
        </div>
        <h1 className="ranking-title">
          {season ? `${season.season} Standings` : 'Bible Contest Rankings'}
        </h1>
        <p className="ranking-subtitle">
          {metric === 'streak'
            ? 'Contestants ranked by consecutive daily gameplay streak.'
            : 'Contestants ranked by highest total score & fastest completion time.'}
        </p>

        {/* Global Leaderboards Metric Switcher (Points vs Streak) */}
        {!seasonId ? (
          <div>
            {/* 1. Primary Metric Toggle (Points vs Daily Streaks) */}
            <div className="ranking-metric-switcher">
              {[
                { id: 'points', label: 'Points Leaderboard', icon: <TrophyIcon size={15} /> },
                { id: 'streak', label: 'Daily Streaks', icon: <StreakFireIcon size={15} /> },
              ].map((m) => {
                const active = metric === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`ranking-metric-btn ${active ? 'active' : ''}`}
                    onClick={() => setMetric(m.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    {m.icon}
                    <span>{m.label}</span>
                  </button>
                );
              })}
            </div>

            {/* 2. Secondary Timeframe Toggle (Week, Last Week, Month, Last Month) - only for Points */}
            {metric === 'points' && (
              <div className="ranking-timeframe-switcher">
                {[
                  { id: 'currentWeek', label: 'Week', icon: <LightningIcon size={13} /> },
                  { id: 'pastWeek', label: 'Last Week', icon: <ClockIcon size={13} /> },
                  { id: 'currentMonth', label: 'Month', icon: <CalendarIcon size={13} /> },
                  { id: 'pastMonth', label: 'Last Month', icon: <CalendarIcon size={13} /> },
                ].map((t) => {
                  const active = timeframe === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`ranking-timeframe-btn ${active ? 'active' : ''}`}
                      onClick={() => setTimeframe(t.id)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                    >
                      {t.icon}
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* Season Leaderboard Timeframe Switcher */
          <div className="ranking-timeframe-switcher">
            {[
              { id: 'weekly', label: 'Weekly', icon: <LightningIcon size={13} /> },
              { id: 'monthly', label: 'Monthly', icon: <CalendarIcon size={13} /> },
              { id: 'overall', label: 'Overall', icon: <TrophyIcon size={13} /> },
            ].map((tab) => {
              const active = seasonTimeframe === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`ranking-timeframe-btn ${active ? 'active' : ''}`}
                  onClick={() => setSeasonTimeframe(tab.id)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <Spinner size="lg" />
        </div>
      ) : standings.length === 0 ? (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: '48px 20px',
            background: 'var(--clr-surface)',
            borderRadius: '20px',
            border: '1px solid var(--clr-border)',
          }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217, 119, 6, 0.1)', color: '#D97706', marginBottom: '16px' }}>
            <TrophyIcon size={30} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 6px 0' }}>No Standings Recorded For This Period</h3>
          <p style={{ color: 'var(--clr-muted)', fontSize: '14px', margin: '0 0 20px 0' }}>
            {metric === 'streak'
              ? 'Play quizzes daily to build your active streak and claim the top ranking!'
              : 'Be the first contestant to complete a round and claim the #1 spot!'}
          </p>
          <Button variant="primary" onClick={() => navigate(seasonId ? `/play/season/${seasonId}` : '/play')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <PlayIcon size={12} />
            <span>Start Playing</span>
          </Button>
        </div>
      ) : (

        <>
          {/* Current User Rank Card (matching Flutter mobile app) */}
          {currentUserEntry && (
            <div
              className="ranking-current-user-card"
              onClick={() => navigate('/profile')}
              style={{ cursor: 'pointer' }}
              title="View your contestant profile & stats"
            >
              <div className="ranking-current-user-left">
                <div className="ranking-current-user-badge">
                  #{currentUserEntry.rank}
                </div>
                <div className="ranking-current-user-info">
                  <div className="ranking-current-user-tag">
                    Your Official Standing
                  </div>
                  <div className="ranking-current-user-name" title={currentUserEntry.userName}>
                    {currentUserEntry.userName}
                  </div>
                </div>
              </div>

              <div className="ranking-current-user-right">
                <div className="ranking-current-user-score">
                  {metric === 'streak'
                    ? `${currentUserEntry.score} days`
                    : `${currentUserEntry.score || currentUserEntry.totalScore || 0} pts`}
                </div>
                <div className="ranking-current-user-sub">
                  {metric === 'streak'
                    ? `peak: ${currentUserEntry.games}d`
                    : `${currentUserEntry.games || currentUserEntry.quizzesCount || 1} games`}
                </div>
              </div>
            </div>
          )}

          {/* Top 3 Podium */}
          {podiumPlayers.length > 0 && (
            <div className="ranking-podium-grid">
              {podiumPlayers.map((player) => {
                const scoreDisplay =
                  metric === 'streak'
                    ? `${player.score} days`
                    : `${player.score ?? player.totalScore ?? 0} pts`;
                const subDisplay =
                  metric === 'streak'
                    ? `peak: ${player.games}d`
                    : `${player.games ?? player.quizzesCount ?? 1} games`;

                return (
                  <div
                    key={player.userId || player.rank}
                    className={`ranking-podium-card ${player.cardClass}`}
                    onClick={() => player.userId && navigate(player.userId === user?.uid ? '/profile' : `/profile/${player.userId}`)}
                    style={{ cursor: player.userId ? 'pointer' : 'default' }}
                    title={player.userId ? `View ${player.userName}'s profile` : ''}
                  >
                    <div className="ranking-podium-medal">{player.medal}</div>
                    <div className="ranking-podium-avatar">
                      {player.img ? (
                        <img
                          src={player.img}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        (player.userName || '?').slice(0, 2).toUpperCase()
                      )}
                    </div>
                    <div className="ranking-podium-name" title={player.userName}>
                      {player.userName}
                    </div>
                    <div className="ranking-podium-score">
                      {scoreDisplay}
                    </div>
                    <div className="ranking-podium-sub">
                      {subDisplay}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Full Ranked List Table */}
          <div className="ranking-table-card">
            <div className="ranking-table-header">
              <span>Contestant</span>
              <span>{metric === 'streak' ? 'Streak / Peak' : 'Score / Games'}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {standings.map((player, idx) => {
                const rank = player.rank || idx + 1;
                const isMe = user?.uid && player.userId === user.uid;
                const rankClass = rank === 1 ? 'rank-gold' : rank === 2 ? 'rank-silver' : rank === 3 ? 'rank-bronze' : '';
                const scoreDisplay =
                  metric === 'streak'
                    ? `${player.score} days`
                    : `${player.score ?? player.totalScore ?? 0} pts`;
                const subDisplay =
                  metric === 'streak'
                    ? `peak: ${player.games}d`
                    : `${player.games ?? player.quizzesCount ?? 1} games`;

                return (
                  <div
                    key={player.userId || idx}
                    className={`ranking-row-item ${isMe ? 'is-current-user' : ''}`}
                    onClick={() => player.userId && navigate(isMe ? '/profile' : `/profile/${player.userId}`)}
                    style={{ cursor: player.userId ? 'pointer' : 'default' }}
                    title={player.userId ? `View ${player.userName}'s profile` : ''}
                  >
                    <div className="ranking-row-left">
                      <span className={`ranking-rank-num ${rankClass}`}>
                        #{rank}
                      </span>

                      <div className="ranking-row-avatar">
                        {player.img ? (
                          <img
                            src={player.img}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          (player.userName || '?').slice(0, 2).toUpperCase()
                        )}
                      </div>

                      <div className="ranking-row-meta">
                        <div className="ranking-player-title" title={player.userName}>
                          {player.userName} {isMe && <span style={{ fontSize: '10.5px', color: 'var(--clr-primary)', fontWeight: 800 }}>(You)</span>}
                        </div>
                        <div className="ranking-player-sub">
                          {subDisplay}
                        </div>
                      </div>
                    </div>

                    <div className="ranking-row-right">
                      <div className="ranking-score-text">
                        {scoreDisplay}
                      </div>
                      {metric === 'points' && player.timeTaken !== undefined && player.timeTaken > 0 && (
                        <div className="ranking-time-text" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <ClockIcon size={12} style={{ color: 'var(--clr-muted)' }} />
                          <span>{Math.floor(player.timeTaken / 60)}m {player.timeTaken % 60}s</span>
                        </div>
                      )}

                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GameLeaderboardPage;
